import { defineStore } from 'pinia'
import { ref } from 'vue'
import {Actor, fetchUserProfile, getActor, UserProfile} from "../services/actorenApi.ts"
import { getKontoSettings, getQRCodes, getRichtprijzen, getRichttijden, KontoSettings } from "../services/kontoApi.ts";
import { findGemeente } from "../services/addressApi.ts";

export const useAppStore = defineStore('app', () => {
    const userProfile = ref({} as UserProfile)
    const organisatie = ref({} as Actor)
    const favorieteExpert = ref<UserProfile | null>(null)
    const bedrijfVanExpert = ref<Actor | null>(null)
    const isOrgAdmin = ref(false)
    const konto = ref({
        richtprijzen: [] as any[],
        richttijden: [] as any[],
        qrCodes: [] as string[],
        gemeente: '' as string,
        kontoSettings: null as KontoSettings | null
    })

    async function initializeUserProfile(up: UserProfile) {
        userProfile.value = up
        if (userProfile.value.verbondenEntiteiten && userProfile.value.verbondenEntiteiten.length > 0) {
            let entity = userProfile.value.verbondenEntiteiten[0]
            let organisatieId = entity.organisatieId
            organisatie.value = await getActor(organisatieId)
            isOrgAdmin.value = entity.relatie === 'ADMIN'
            // TODO : ook niet geconfirmeerde ADMINS hebben reeds ADMIN rechten!
        }
    }

    async function initializeUserData() {
        if (!userProfile.value?.konto?.id) {
            return
        }
        if (userProfile.value.nieuw) {
            return
        }
        try {
            // Parallel API calls for faster performance
            const [richtprijzen, richttijden, qrcodes, kontoSettings] = await Promise.all([
                getRichtprijzen(userProfile.value.konto.id),
                getRichttijden(userProfile.value.konto.id),
                getQRCodes(userProfile.value.konto.id),
                getKontoSettings(userProfile.value.konto.id)
            ])
            konto.value.richtprijzen = richtprijzen;
            konto.value.richttijden = richttijden;
            konto.value.qrCodes = qrcodes;
            konto.value.kontoSettings = kontoSettings;
            if (kontoSettings?.nisOperationeel) {
                konto.value.gemeente = await findGemeente(kontoSettings.nisOperationeel);
            }
            if (userProfile.value.gekozenUitvoerder) {
                favorieteExpert.value = await fetchUserProfile(userProfile.value.gekozenUitvoerder);
                // indien de expert met een bedrijf werkt.
                if (favorieteExpert.value?.verbondenEntiteiten.length > 0) {
                    bedrijfVanExpert.value = await getActor(favorieteExpert.value.verbondenEntiteiten[0].organisatieId);
                }
            }
        } catch (error) {
            console.error('Failed to load user data', error)
        }
    }

    async function checkBackendSession(): Promise<boolean> {
        try {
            const res = await fetch('/api/actoren/me', {credentials: 'include', redirect: 'manual'})

            // If the backend issues a redirect (e.g., to its OAuth login), forward it.
            if (res.type === 'opaqueredirect' || res.status === 0) {
                return false
            }

            const identiteit: UserProfile = await res.json()
            const appStore = useAppStore();
            await appStore.initializeUserProfile(identiteit)

            return res.ok
        } catch {
            return false
        }
    }

    function getRichtprijs(typologie: string, discipline: string) {
        if (!konto.value.richtprijzen || !Array.isArray(konto.value.richtprijzen)) return 0;
        if (!typologie || !discipline) return 0;
        const entry = konto.value.richtprijzen.find((rp: any) => rp.typologie === typologie);
        return entry ? (entry[discipline] || 0) : 0;
    }

    // return: 2 uur default
    function getRichttijd(typologie: string, discipline: string) {
        if (!konto.value.richttijden || !Array.isArray(konto.value.richttijden)) return 0;
        if (!typologie || !discipline) return 0;
        const entry = konto.value.richttijden.find((rt: any) => rt.typologie === typologie);
        return entry ? (entry[discipline] || 0) : 0;
    }


    return {
        userProfile,
        organisatie,
        isOrgAdmin,
        konto,
        favorieteExpert,
        initializeUserProfile,
        initializeUserData,
        checkBackendSession,
        getRichtprijs,
        getRichttijd
    }
})
